import { makeStyles } from "@material-ui/core";
import "./reportFonts";
import React, { useState } from "react";
import { useHistory } from "react-router-dom";
import { REPORT_SECTIONS, REPORT_ICONS } from "./reportConfig";

const PAGE_BG = "#F0F2F5";
const NAV = "#0D2137";

const useStyles = makeStyles(() => ({
  page: {
    backgroundColor: PAGE_BG,
    minHeight: "calc(100vh - 80px)",
    padding: "0",
  },
  pageHeader: {
    background: "#fff",
    borderBottom: "1px solid #E0E5EC",
    padding: "18px 28px 14px",
    display: "flex",
    alignItems: "center",
    gap: "14px",
  },
  headerText: {
    display: "flex",
    alignItems: "baseline",
    gap: "10px",
    minWidth: 0,
  },
  pageTitle: {
    fontSize: "20px",
    fontWeight: 700,
    color: NAV,
    margin: 0,
  },
  pageSubtitle: {
    fontSize: "13px",
    color: "#78909C",
    margin: 0,
  },
  body: {
    padding: "24px 28px 32px",
  },
  sectionLabel: {
    fontSize: "13px",
    fontWeight: 800,
    letterSpacing: "0.10em",
    textTransform: "uppercase",
    color: "#0D2137",
    marginBottom: "12px",
    marginTop: "6px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    "&::before": {
      content: '""',
      width: "3px",
      height: "14px",
      borderRadius: "2px",
      background: "#1565C0",
      flexShrink: 0,
    },
    "&::after": {
      content: '""',
      flex: 1,
      height: "2px",
      borderRadius: "2px",
      background: "linear-gradient(90deg, #1565C0 0%, #E0E5EC 50%)",
    },
  },
  reportGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
    gap: "16px",
    marginBottom: "28px",
  },
  card: {
    position: "relative",
    backgroundColor: "#fff",
    borderRadius: "12px",
    border: "1.5px solid #E0E5EC",
    boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
    padding: "16px 16px 14px",
    display: "flex",
    flexDirection: "column",
    gap: "0",
    transition: "box-shadow 0.18s ease, border-color 0.18s ease",
    "&:hover": {
      boxShadow: "0 4px 16px rgba(13,33,55,0.12)",
      borderColor: "#B0BEC5",
    },
  },
  cardPinned: {
    borderColor: "#1565C0",
  },
  cardTopRow: {
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: "12px",
  },
  cardIconWrap: {
    width: "38px",
    height: "38px",
    borderRadius: "9px",
    background: "#EBF3FF",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  pinBtn: {
    width: "32px",
    height: "32px",
    borderRadius: "7px",
    border: "1.5px solid #E0E5EC",
    background: "#fff",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
    cursor: "pointer",
    transition: "background 0.15s ease, border-color 0.15s ease",
    "&:hover": {
      background: "#F5F7FA",
      borderColor: "#B0BEC5",
    },
  },
  pinBtnActive: {
    background: "#EBF3FF",
    borderColor: "#1565C0",
  },
  cardTitle: {
    fontSize: "14px",
    fontWeight: 700,
    color: "#0D2137",
    lineHeight: 1.35,
    marginBottom: "6px",
  },
  cardDesc: {
    fontSize: "12.5px",
    color: "#78909C",
    lineHeight: 1.5,
    flex: 1,
  },
  cardDivider: {
    height: "1px",
    background: "#E0E5EC",
    margin: "14px 0 12px",
  },
  cardFooter: {
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
  },
  runBtn: {
    display: "flex",
    alignItems: "center",
    gap: "5px",
    padding: "5px 14px",
    borderRadius: "7px",
    border: "1.5px solid #CDD5E0",
    background: "#fff",
    fontSize: "13px",
    fontWeight: 600,
    color: "#0D2137",
    cursor: "pointer",
    transition: "background 0.15s ease, border-color 0.15s ease",
    "&:hover": {
      background: "#F5F7FA",
      borderColor: "#90A4AE",
    },
  },
}));

function PinIcon({ filled }) {
  const color = filled ? "#1565C0" : "#78909C";
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="12" y1="17" x2="12" y2="22" />
      <path
        d="M5 17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24Z"
        fill={filled ? "#1565C0" : "none"}
      />
    </svg>
  );
}

function RunIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor">
      <polygon points="5,3 19,12 5,21" />
    </svg>
  );
}

function ReportCard({ report, pinned, onTogglePin, onRun }) {
  const classes = useStyles();

  return (
    <div className={`${classes.card} ${pinned ? classes.cardPinned : ""}`}>
      <div className={classes.cardTopRow}>
        <div className={classes.cardIconWrap}>{REPORT_ICONS[report.icon]}</div>
        <button
          type="button"
          className={`${classes.pinBtn} ${pinned ? classes.pinBtnActive : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onTogglePin(report.key);
          }}
          title={pinned ? "Unpin Report" : "Pin Report"}
        >
          <PinIcon filled={pinned} />
        </button>
      </div>

      <div className={classes.cardTitle}>{report.label}</div>
      <div className={classes.cardDesc}>{report.description}</div>

      <div className={classes.cardDivider} />

      <div className={classes.cardFooter}>
        <button type="button" className={classes.runBtn} onClick={() => onRun(report)}>
          Run <RunIcon />
        </button>
      </div>
    </div>
  );
}

export default function ReportsCatalog() {
  const classes = useStyles();
  const history = useHistory();

  const [pinnedKeys, setPinnedKeys] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("pinnedReports") || "[]");
    } catch {
      return [];
    }
  });

  const togglePin = (key) => {
    setPinnedKeys((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : [...prev, key];
      localStorage.setItem("pinnedReports", JSON.stringify(next));
      return next;
    });
  };

  const openReport = (report) => {
    history.push(`/projects/reports/${report.key}`);
  };

  const allReports = REPORT_SECTIONS.flatMap((s) => s.reports);
  const pinnedReports = allReports.filter((r) => pinnedKeys.includes(r.key));

  return (
    <div className={classes.page}>
      <div className={classes.pageHeader}>
        <div className={classes.headerText}>
          <h1 className={classes.pageTitle}>Reports</h1>
          <span className={classes.pageSubtitle}>
            Automated Report Pack — Project Revenue, Cost &amp; Margin
          </span>
        </div>
      </div>

      <div className={classes.body}>
        {pinnedReports.length > 0 && (
          <div>
            <div className={classes.sectionLabel}>Pinned</div>
            <div className={classes.reportGrid}>
              {pinnedReports.map((report) => (
                <ReportCard
                  key={report.key}
                  report={report}
                  pinned
                  onTogglePin={togglePin}
                  onRun={openReport}
                />
              ))}
            </div>
          </div>
        )}

        {REPORT_SECTIONS.map((section) => {
          const unpinnedReports = section.reports.filter(
            (r) => !pinnedKeys.includes(r.key),
          );
          if (unpinnedReports.length === 0) return null;
          return (
            <div key={section.label}>
              <div className={classes.sectionLabel}>{section.label}</div>
              <div className={classes.reportGrid}>
                {unpinnedReports.map((report) => (
                  <ReportCard
                    key={report.key}
                    report={report}
                    pinned={false}
                    onTogglePin={togglePin}
                    onRun={openReport}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
